import React, { useMemo } from 'react';
import { Tooltip } from 'antd';

interface ConsistencyScoreProps {
  score?: number; // Điểm số từ 0 đến 100 (Có thể truyền số thập phân, vd: 26.61)
}

// Hàm hỗ trợ tính toán tọa độ theo góc
const getCoordinates = (cx: number, cy: number, radius: number, angleInDegrees: number) => {
  const rad = (angleInDegrees * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(rad),
    y: cy - radius * Math.sin(rad),
  };
};

// Hàm interpolate màu hex sang RGB
const interpolateColor = (color1: string, color2: string, factor: number) => {
  const c1 = parseInt(color1.slice(1), 16);
  const c2 = parseInt(color2.slice(1), 16);

  const r1 = (c1 >> 16) & 0xff, g1 = (c1 >> 8) & 0xff, b1 = c1 & 0xff;
  const r2 = (c2 >> 16) & 0xff, g2 = (c2 >> 8) & 0xff, b2 = c2 & 0xff;

  const r = Math.round(r1 + factor * (r2 - r1));
  const g = Math.round(g1 + factor * (g2 - g1));
  const b = Math.round(b1 + factor * (b2 - b1));

  return `rgb(${r}, ${g}, ${b})`;
};

// Hàm lấy màu chuyển tiếp mượt mà tại điểm phần trăm (0 -> 100)
const getGradientColor = (percent: number, COLORS: { danger: string; warning: string; success: string }) => {
  if (percent <= 30) {
    const factor = percent / 30;
    return interpolateColor(COLORS.danger, COLORS.warning, factor);
  } else if (percent <= 80) {
    const factor = (percent - 30) / 50;
    return interpolateColor(COLORS.warning, COLORS.success, factor);
  } else {
    return COLORS.success;
  }
};

export const ConsistencyScore: React.FC<ConsistencyScoreProps> = ({ score = 26.61 }) => {
  const safeScore = Math.min(Math.max(score*100, 0), 100);

  // --- CẤU HÌNH MÀU SẮC ---
  const COLORS = {
    danger: '#ff4d4f',   // Đỏ 0-30%
    warning: '#faad14',  // Cam 30-80%
    success: '#13c2c2',  // Xanh ngọc 80-100%
    inactive: '#94a3b8'  // Xám tro cho vạch chưa đạt
  };

  // Xác định tier hiện tại (màu sắc và text đánh giá)
  const currentTier = useMemo(() => {
    const dynamicColor = getGradientColor(safeScore, COLORS);
    if (safeScore <= 30) return { color: dynamicColor, label: 'Poor' };
    if (safeScore <= 80) return { color: dynamicColor, label: 'Average' };
    return { color: dynamicColor, label: 'Good' };
  }, [safeScore]);

  // --- CẤU HÌNH BIỂU ĐỒ TRỤC ---
  const cx = 150;      // Tọa độ X tâm (ViewBox rộng 300)
  const cy = 150;      // Tọa độ Y tâm
  const rOuter = 100;  // Bán kính ngoài của vạch
  const rInner = 80;   // Bán kính trong của vạch
  const rText = 120;   // Bán kính vòng chữ % bên ngoài

  // 1. Tính toán Vạch (Ticks)
  const ticks = useMemo(() => {
    const lines = [];

    for (let i = 0; i <= 100; i++) {

      const angle = 180 - (i / 100) * 180;

      const start = getCoordinates(cx, cy, rOuter, angle);
      const end = getCoordinates(cx, cy, rInner, angle);

      // Logic màu vạch: Vượt quá điểm thì xám, đạt điểm thì gradient chuyển màu từ từ
      let strokeColor = COLORS.inactive;
      if (i <= safeScore) {
        strokeColor = getGradientColor(i, COLORS);
      }

      lines.push(
        <line
          key={`tick-${i}`}
          x1={start.x}
          y1={start.y}
          x2={end.x}
          y2={end.y}
          stroke={strokeColor}
          strokeWidth="1.8"
          strokeLinecap="round"
          className="transition-colors duration-500 ease-in-out"
        />
      );
    }
    return lines;
  }, [safeScore]);

  // 2. Tính toán Nhãn Text Bên Ngoài (Outer Labels)
  const labelsToRender = [0, 30, 50, 80, 100];
  const outerLabels = labelsToRender.map((val) => {
    const angle = 180 - (val / 100) * 180;
    const pos = getCoordinates(cx, cy, rText, angle);

    // Tinh chỉnh text-anchor để chữ không bị tràn khung
    let textAnchor: "middle" | "end" | "start" = "middle";
    if (val === 0) textAnchor = "end";
    if (val === 100) textAnchor = "start";

    return (
      <text
        key={`label-${val}`}
        x={pos.x}
        y={pos.y + 4} // +4 để chữ cân đối quang học so với tọa độ Y
        fill="#94a3b8"
        fontSize="11"
        fontWeight="600"
        textAnchor={textAnchor}
      >
        {val}%
      </text>
    );
  });

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm w-full h-full max-w-md p-6 relative overflow-hidden">

      {/* Hiệu ứng Phát sáng dưới đáy (Glow/Shadow) */}
      <div className="absolute bottom-[-40px] left-1/2 transform -translate-x-1/2 w-3/4 h-32 blur-3xl opacity-40 rounded-full transition-colors duration-700 pointer-events-none"
        style={{
          backgroundColor: currentTier.color,
          filter: 'blur(60px)',
          opacity: 0.35
        }}></div>

      {/* --- HEADER --- */}
      <div className="flex justify-between items-center mb-2 z-10 relative">
        <h6 className="text-lg font-bold text-gray-900 m-0">Điểm nhất quán</h6>

        {/* Nút Tooltip vuông bo góc giống ảnh */}
        <Tooltip title="Giá trị này đo lường mức độ nhất quán trong hành vi giao dịch của bạn dựa trên kết quả của mỗi phiên giao dịch. Giá trị càng cao cho thấy giao dịch của bạn càng ổn định và nhất quán hơn.">
          <div className="w-8 h-8 rounded border border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-50">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 16v-4"></path>
              <path d="M12 8h.01"></path>
            </svg>
          </div>
        </Tooltip>
      </div>

      {/* --- LEGEND --- */}
      <div className="flex gap-4 text-[13px] font-medium text-gray-500 mb-8 z-10 relative">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS.danger }}></span>
          <span>0 - 30%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS.warning }}></span>
          <span>30 - 80%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS.success }}></span>
          <span>80 - 100%</span>
        </div>
      </div>

      {/* --- ĐỒ THỊ --- */}
      <div className="w-full flex justify-center z-10 relative">
        <div className="w-full max-w-[340px]">
          {/* viewBox 300x170 tạo khoảng không đủ rộng rãi cho text bên ngoài */}
          <svg viewBox="0 0 300 170" className="w-full h-auto overflow-visible">

            {/* Render các Vạch */}
            {ticks}

            {/* Render các Label (0%, 30%, 50%...) */}
            {outerLabels}

            {/* Thông tin ở giữa trung tâm (Điểm & Đánh giá) */}
            <text
              x={cx}
              y={cy - 10}
              textAnchor="middle"
              className="!text-[18px] !font-bold fill-current !text-gray-700"
            >
              {/* Định dạng số có 2 chữ số thập phân */}
              {safeScore.toFixed(2)}%
            </text>

            <text
              x={cx}
              y={cy + 16}
              textAnchor="middle"
              fontSize="14"
              fontWeight="600"
              fill={currentTier.color} // Màu text thay đổi theo tier
              className="transition-colors duration-500"
            >
              {currentTier.label}
            </text>

          </svg>
        </div>
      </div>

    </div >
  );
};
export default ConsistencyScore